import { useState, useEffect, useCallback } from 'react';
import { money, moneyCompact } from './format.js';
import { api } from './apiClient.js';

export const SUPPORTED_CURRENCIES = [
  { code: 'INR', symbol: '₹', label: 'INR (₹) - Indian Rupee' },
  { code: 'USD', symbol: '$', label: 'USD ($) - US Dollar' },
  { code: 'EUR', symbol: '€', label: 'EUR (€) - Euro' },
  { code: 'GBP', symbol: '£', label: 'GBP (£) - British Pound' },
  { code: 'AED', symbol: 'AED', label: 'AED (د.إ) - UAE Dirham' },
  { code: 'CAD', symbol: '$', label: 'CAD ($) - Canadian Dollar' },
  { code: 'AUD', symbol: '$', label: 'AUD ($) - Australian Dollar' },
  { code: 'SGD', symbol: '$', label: 'SGD ($) - Singapore Dollar' },
];

export const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AED: 'AED',
  CAD: '$',
  AUD: '$',
  SGD: '$',
};

// Global market exchange rates relative to 1 USD
export const EXCHANGE_RATES_TO_USD = {
  USD: 1.0,
  INR: 83.50,   // 1 USD = 83.50 INR  => 1 INR = ~0.01198 USD
  EUR: 0.92,    // 1 USD = 0.92 EUR   => 1 EUR = ~90.76 INR
  GBP: 0.79,    // 1 USD = 0.79 GBP   => 1 GBP = ~105.70 INR
  AED: 3.6725,  // 1 USD = 3.6725 AED => 1 AED = ~22.74 INR
  CAD: 1.36,    // 1 USD = 1.36 CAD   => 1 CAD = ~61.40 INR
  AUD: 1.53,    // 1 USD = 1.53 AUD   => 1 AUD = ~54.58 INR
  SGD: 1.34,    // 1 USD = 1.34 SGD   => 1 SGD = ~62.31 INR
};

const STORAGE_KEY = 'nexerp_preferred_currency';
const STORAGE_RATES_KEY = 'nexerp_backend_exchange_rates';

// Load cached corporate rates from localStorage immediately on startup
if (typeof window !== 'undefined') {
  try {
    const cached = localStorage.getItem(STORAGE_RATES_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object') {
        Object.assign(EXCHANGE_RATES_TO_USD, parsed);
      }
    }
  } catch {
    // ignore
  }
}

let syncPromise = null;
let lastSyncTimestamp = 0;

/**
 * Fetches the tenant's official corporate exchange rates from backend MDM service.
 * Automatically caches in memory and localStorage.
 */
export async function syncCorporateRatesFromBackend(force = false) {
  if (typeof window === 'undefined') return EXCHANGE_RATES_TO_USD;
  const now = Date.now();
  // Don't spam: check at most once every 5 minutes unless forced
  if (!force && now - lastSyncTimestamp < 5 * 60 * 1000) {
    return EXCHANGE_RATES_TO_USD;
  }
  if (syncPromise) return syncPromise;

  syncPromise = (async () => {
    try {
      const res = await api.get('/mdm/currencies/active-rates');
      const rates = res?.data?.ratesToUSD || res?.ratesToUSD;
      if (rates && typeof rates === 'object') {
        Object.assign(EXCHANGE_RATES_TO_USD, rates);
        lastSyncTimestamp = Date.now();
        try {
          localStorage.setItem(STORAGE_RATES_KEY, JSON.stringify(rates));
        } catch {
          // ignore
        }
        window.dispatchEvent(new CustomEvent('nexerp:rates_updated', { detail: EXCHANGE_RATES_TO_USD }));
      }
    } catch {
      // Graceful fallback to existing database-cached or baseline rates
    } finally {
      syncPromise = null;
    }
    return EXCHANGE_RATES_TO_USD;
  })();

  return syncPromise;
}

// Auto-trigger sync on load in browser
if (typeof window !== 'undefined') {
  setTimeout(() => {
    syncCorporateRatesFromBackend().catch(() => {});
  }, 100);
}

export function getStoredCurrency() {
  if (typeof window === 'undefined') return 'INR';
  try {
    return localStorage.getItem(STORAGE_KEY) || 'INR';
  } catch {
    return 'INR';
  }
}

export function setStoredCurrency(code) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, code);
    window.dispatchEvent(new CustomEvent('nexerp:currency_change', { detail: code }));
  } catch {
    // ignore
  }
}

/**
 * Normalizes effective source currency.
 * Ensures provided currencies (e.g. USD, EUR, INR, GBP, AED) are accurately recognized
 * without erroneously treating valid multi-currency amounts as INR.
 */
export function getEffectiveSourceCurrency(currency, _amount) {
  if (currency && typeof currency === 'string' && currency.trim()) {
    return currency.trim().toUpperCase();
  }
  return 'INR';
}

/**
 * Converts a numeric value from source currency to target currency using exchange rates.
 */
export function convertCurrency(amount, fromCurrency = 'INR', toCurrency = 'INR') {
  const num = Number(amount || 0);
  if (Number.isNaN(num) || num === 0) return 0;

  const effectiveFrom = getEffectiveSourceCurrency(fromCurrency, num).toUpperCase();
  const effectiveTo = (toCurrency || 'INR').toUpperCase();

  if (effectiveFrom === effectiveTo) return num;

  const rateFrom = EXCHANGE_RATES_TO_USD[effectiveFrom] || 1.0;
  const rateTo = EXCHANGE_RATES_TO_USD[effectiveTo] || 1.0;

  // Convert: from -> USD -> to
  const inUSD = num / rateFrom;
  const converted = inUSD * rateTo;

  return Math.round(converted * 100) / 100;
}

/**
 * Formats a monetary value into the target currency with automatic exchange rate conversion.
 */
export function formatCurrency(amount, targetCurrencyCode, sourceCurrencyCode = 'INR') {
  const target = targetCurrencyCode || getStoredCurrency();
  const source = sourceCurrencyCode || 'INR';
  const converted = convertCurrency(amount, source, target);
  return money(converted, target);
}

export function formatCurrencyCompact(amount, targetCurrencyCode, sourceCurrencyCode = 'INR') {
  const target = targetCurrencyCode || getStoredCurrency();
  const source = sourceCurrencyCode || 'INR';
  const converted = convertCurrency(amount, source, target);
  return moneyCompact(converted, target);
}

export function getCurrencySymbol(currencyCode) {
  const code = currencyCode || getStoredCurrency();
  return CURRENCY_SYMBOLS[code] || code;
}

/**
 * React hook for live currency synchronization across all screens.
 * When any component updates the currency, all subscribers immediately re-render with converted values.
 */
export function useCurrency() {
  const [currency, setCurrencyState] = useState(getStoredCurrency());
  const [rates, setRatesState] = useState({ ...EXCHANGE_RATES_TO_USD });

  useEffect(() => {
    const handleCurrencyChange = (e) => {
      setCurrencyState(e.detail || getStoredCurrency());
    };
    const handleRatesChange = (e) => {
      if (e.detail) {
        setRatesState({ ...e.detail });
      }
    };

    window.addEventListener('nexerp:currency_change', handleCurrencyChange);
    window.addEventListener('nexerp:rates_updated', handleRatesChange);

    // Trigger background sync with backend MDM service if needed
    syncCorporateRatesFromBackend().catch(() => {});

    return () => {
      window.removeEventListener('nexerp:currency_change', handleCurrencyChange);
      window.removeEventListener('nexerp:rates_updated', handleRatesChange);
    };
  }, []);

  const changeCurrency = useCallback((newCurrency) => {
    setCurrencyState(newCurrency);
    setStoredCurrency(newCurrency);
  }, []);

  const format = useCallback((amount, maybeTargetOrSource = 'INR', maybeSource) => {
    if (maybeSource !== undefined) {
      return formatCurrency(amount, maybeTargetOrSource || currency, maybeSource);
    }
    return formatCurrency(amount, currency, maybeTargetOrSource || 'INR');
  }, [currency, rates]);

  const convert = useCallback((amount, sourceCurrency = 'INR') => {
    return convertCurrency(amount, sourceCurrency, currency);
  }, [currency, rates]);

  const formatConverted = useCallback((amount) => {
    return money(amount, currency);
  }, [currency]);

  const formatCompact = useCallback((amount, sourceCurrency = 'INR') => {
    return formatCurrencyCompact(amount, currency, sourceCurrency);
  }, [currency, rates]);

  return {
    currency,
    setCurrency: changeCurrency,
    format,
    convert,
    formatConverted,
    formatCompact,
    symbol: getCurrencySymbol(currency),
    currencies: SUPPORTED_CURRENCIES,
    rates,
    syncRates: syncCorporateRatesFromBackend,
  };
}

const LIVE_RATE_CACHE = new Map();
const LIVE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches real-time market exchange rate for a currency pair (e.g. USD -> INR).
 * Queries free live APIs (ExchangeRate-API with fallback to Fawaz Ahmed CDN).
 * Returns { rate: number, formatted: string, from: string, to: string } or null.
 */
export async function fetchLiveExchangeRate(from, to, force = false) {
  const fromCur = (from || '').trim().toUpperCase();
  const toCur = (to || '').trim().toUpperCase();
  if (!fromCur || !toCur || fromCur.length !== 3 || toCur.length !== 3) return null;

  if (fromCur === toCur) {
    return { rate: 1, formatted: '1.000000', from: fromCur, to: toCur };
  }

  const cacheKey = `${fromCur}:${toCur}`;
  const now = Date.now();
  if (!force && LIVE_RATE_CACHE.has(cacheKey)) {
    const cached = LIVE_RATE_CACHE.get(cacheKey);
    if (now - cached.time < LIVE_CACHE_TTL) {
      return cached.data;
    }
  }

  let rate = null;

  // 1. Primary: ExchangeRate-API (free, CORS-enabled, no key required)
  try {
    const res = await fetch(`https://api.exchangerate-api.com/v4/latest/${fromCur}`);
    if (res.ok) {
      const data = await res.json();
      if (data?.rates && data.rates[toCur] != null) {
        rate = data.rates[toCur];
      }
    }
  } catch {
    // fallback
  }

  // 2. Secondary fallback: Fawaz Ahmed Open Currency CDN
  if (rate == null) {
    try {
      const res = await fetch(
        `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${fromCur.toLowerCase()}.json`
      );
      if (res.ok) {
        const data = await res.json();
        const ratesObj = data?.[fromCur.toLowerCase()];
        if (ratesObj && ratesObj[toCur.toLowerCase()] != null) {
          rate = ratesObj[toCur.toLowerCase()];
        }
      }
    } catch {
      // ignore
    }
  }

  if (rate != null) {
    const formatted = Number.isInteger(rate)
      ? rate.toString()
      : parseFloat(Number(rate).toFixed(6)).toString();
    const result = { rate, formatted, from: fromCur, to: toCur };
    LIVE_RATE_CACHE.set(cacheKey, { time: now, data: result });
    return result;
  }

  return null;
}
